import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { KeyHolder, KeyType, RegisterKey } from '../../types/keyRegister';
import { useKeyObjects } from '../../hooks/keyRegister';
import { typeObjectLinks } from '../../Helpers/keyRegisterUtils';
import { KeyObjectsByBuilding } from './keyObjects';
import { KeySelection, parseSelection, selectionParams } from './keySelection';
import {
    buildSections,
    ExpandOverrides,
    isFiltering,
    isKeyInScope,
    isSectionExpanded,
    KeyScopeOptions,
    KeyStatusFilter,
    KeySummary,
    KeyTypeSection,
    parseStatusFilter,
    summarizeKeys,
} from './keyRegisterView';

interface UseKeyRegisterViewInput {
    types: KeyType[];
    keys: RegisterKey[];
    typesByKey: { [typeKey: string]: KeyType };
    keysByType: { [typeKey: string]: RegisterKey[] };
    today: string;
    holderName: (holder: KeyHolder | undefined) => string;
    /** Every building, loaded once the filter tree is first opened. */
    allBuildingKeys: string[];
}

export interface UseKeyRegisterViewReturn {
    /** `?q=` */
    query: string;
    setQuery: (query: string) => void;
    /** `?status=` (a summary card) */
    statusFilter: KeyStatusFilter | null;
    /** Sets the filter, or clears it when it is already the active one; null clears. */
    toggleStatusFilter: (filter: KeyStatusFilter | null) => void;
    clearFilters: () => void;
    filtering: boolean;
    /** `?in=` (the filter tree): narrows the cards and the sections to the types matching a pick. */
    selection: KeySelection;
    setSelection: (selection: KeySelection) => void;
    options: KeyScopeOptions;
    setOptions: (options: KeyScopeOptions) => void;
    /** Card counts: keys in scope (of the selection), regardless of search and status filter. */
    summary: KeySummary;
    sections: KeyTypeSection[];
    isExpanded: (typeKey: string) => boolean;
    toggleExpanded: (typeKey: string) => void;
    /** `?key=` */
    selectedKeyKey: string | null;
    selectKey: (keyKey: string | null) => void;
    /** The picked buildings' objects, those of every building a type links objects in, and every building's once `loadAllObjects` ran. */
    objectsByBuilding: KeyObjectsByBuilding;
    loadingBuildings: { [buildingKey: string]: true };
    /** Loads every building's objects (the filter tree). */
    loadAllObjects: () => void;
}

/** Writes the picks as `?in=`, dropping the older `?building=` / `&object=`. */
const writeSelection = (next: URLSearchParams, value: KeySelection) => {
    next.delete('building');
    next.delete('object');
    next.delete('in');
    for (const v of selectionParams(value)) next.append('in', v);
};

/**
 * View state of the nyckelregister page: search, card filter and the filter tree's picks in the URL
 * (`?q=`, `?status=`, `?in=`, next to `?key=`; the older `?building=` / `&object=` links are read and
 * rewritten to `?in=`), the scope toggles, and which type sections are open. Manual open/closed
 * choices are kept apart from those made while filtering, so clearing a search restores them.
 */
export const useKeyRegisterView = ({ types, keys, typesByKey, keysByType, today, holderName, allBuildingKeys }: UseKeyRegisterViewInput): UseKeyRegisterViewReturn => {
    const [params, setParams] = useSearchParams();
    const [options, setOptions] = useState<KeyScopeOptions>({ showScrapped: false, showArchived: false });
    const [manual, setManual] = useState<ExpandOverrides>({});
    const [whileFiltering, setWhileFiltering] = useState<ExpandOverrides>({});
    const [loadAll, setLoadAll] = useState(false);

    const query = params.get('q') ?? '';
    const statusFilter = parseStatusFilter(params.get('status'));
    const selection = useMemo(() => parseSelection(params), [params]);
    const selectedKeyKey = params.get('key');
    const filtering = isFiltering(query, statusFilter);

    const updateParams = useCallback((change: (next: URLSearchParams) => void, replace: boolean) => {
        setParams(prev => {
            const next = new URLSearchParams(prev);
            change(next);
            return next;
        }, { replace });
    }, [setParams]);

    // The building page's `?building=` and an object's `?building=&object=` links become `?in=`.
    const legacy = params.has('building');
    useEffect(() => {
        if (legacy) updateParams(next => writeSelection(next, parseSelection(next)), true);
    }, [legacy, updateParams]);

    const setSelection = useCallback((value: KeySelection) => updateParams(next => writeSelection(next, value), false), [updateParams]);

    const setQuery = useCallback((value: string) => updateParams(next => {
        if (value) next.set('q', value); else next.delete('q');
    }, true), [updateParams]);

    const toggleStatusFilter = useCallback((filter: KeyStatusFilter | null) => updateParams(next => {
        if (filter && next.get('status') !== filter) next.set('status', filter); else next.delete('status');
    }, false), [updateParams]);

    const clearFilters = useCallback(() => updateParams(next => {
        next.delete('q');
        next.delete('status');
    }, false), [updateParams]);

    const selectKey = useCallback((keyKey: string | null) => updateParams(next => {
        if (keyKey) next.set('key', keyKey); else next.delete('key');
    }, !keyKey), [updateParams]);

    const objectBuildingKeys = useMemo(() => {
        const needed = new Set<string>(loadAll ? allBuildingKeys : []);
        for (const buildingKey of Object.keys(selection)) needed.add(buildingKey);
        for (const type of types) for (const link of typeObjectLinks(type)) needed.add(link.buildingKey);
        return Array.from(needed);
    }, [types, selection, loadAll, allBuildingKeys]);
    const { objectsByBuilding, loadingBuildings } = useKeyObjects(objectBuildingKeys);

    // Choices made while filtering last only as long as the filter.
    useEffect(() => {
        if (!filtering) setWhileFiltering({});
    }, [filtering]);

    // A `?key=` link opens that key's type section.
    const selectedTypeKey = selectedKeyKey ? keys.find(k => k.key === selectedKeyKey)?.typeKey : undefined;
    useEffect(() => {
        if (selectedTypeKey) setManual(prev => (prev[selectedTypeKey] ? prev : { ...prev, [selectedTypeKey]: true }));
    }, [selectedTypeKey]);

    const summary = useMemo(
        () => summarizeKeys(keys.filter(key => isKeyInScope(key, typesByKey, options, selection)), today),
        [keys, typesByKey, options, selection, today]
    );

    const sections = useMemo(() => buildSections({
        types,
        keysByType,
        options,
        selection,
        query,
        statusFilter,
        today,
        holderNameOf: key => (key.holder ? holderName(key.holder) : undefined),
    }), [types, keysByType, options, selection, query, statusFilter, today, holderName]);

    const visibleCount = sections.length;
    const isExpanded = useCallback(
        (typeKey: string) => isSectionExpanded(typeKey, { filtering, visibleCount, manual, whileFiltering }),
        [filtering, visibleCount, manual, whileFiltering]
    );
    const toggleExpanded = useCallback((typeKey: string) => {
        const open = isExpanded(typeKey);
        if (filtering) setWhileFiltering(prev => ({ ...prev, [typeKey]: !open }));
        else setManual(prev => ({ ...prev, [typeKey]: !open }));
    }, [filtering, isExpanded]);

    return {
        query,
        setQuery,
        statusFilter,
        toggleStatusFilter,
        clearFilters,
        filtering,
        selection,
        setSelection,
        options,
        setOptions,
        summary,
        sections,
        isExpanded,
        toggleExpanded,
        selectedKeyKey,
        selectKey,
        objectsByBuilding,
        loadingBuildings,
        loadAllObjects: useCallback(() => setLoadAll(true), []),
    };
};
